import { signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { useTranslation } from 'preact-i18next';
import { AppStore } from '../stores/AppStore';
import { useDialogState } from '../hooks/useDialogState';
import {
  findMatchingColumns,
  reconcileKeyPairs,
  getColumnsForTarget,
  analyzeJoinKeys,
  previewJoin,
  type KeyPairAnalysis,
  type MismatchPreview,
} from '../handlers/transform/join-handlers';
import formStyles from './form-controls.module.css';
import colStyles from './column-editor.module.css';
import exprStyles from './expression-help.module.css';
const styles = { ...formStyles, ...colStyles, ...exprStyles };
import joinStyles from './JoinDialog.module.css';
import { JoinTreeSelector } from './JoinTreeSelector';
import { TablePreviewModal } from './TablePreviewModal';
import { JoinTypeSelector, JoinKeysEditor, JoinColumnSelector } from './join';
import type { JoinType, JoinTarget } from '../../types/modes';

// Re-export for backward compatibility
export type { JoinType, JoinTarget } from '../../types/modes';

export function JoinDialog() {
  const { t } = useTranslation('dialogs');

  const { state } = useDialogState(
    (ctx) => {
      const models = AppStore.models.value;
      const sources = AppStore.sources.value;
      const activeModel = AppStore.activeModel.value;
      const activeSource = AppStore.activeSource.value;

      // Build available targets
      const availableTargets: JoinTarget[] = [];
      models.forEach((model) => {
        if (activeModel && model.id !== activeModel.id) {
          availableTargets.push({
            id: model.id,
            name: model.name,
            type: 'model',
            sourceName: sources.find((s) => s.id === model.sourceId)?.name || 'Unknown',
          });
        }
      });
      sources.forEach((source) => {
        availableTargets.push({
          id: source.id,
          name: source.name,
          type: 'source',
          sourceName: source.name,
        });
      });

      const leftModelId = activeModel?.id || activeSource?.id || null;
      const leftCols = leftModelId ? getColumnsForTarget(leftModelId) : [];

      // Check for editing — join has 4 step types
      const editJoin = ctx.editingStep?.join;
      const editSemijoin = ctx.editingStep?.semijoin;
      const editAntijoin = ctx.editingStep?.antijoin;
      const editLookup = ctx.editingStep?.lookup;

      let initialRightModel: string | null;
      let initialJoinType: JoinType;
      let initialKeyPairs: (string | null)[][];
      let initialSuffixes: string[];
      let rightCols: string[];
      let initialSelectedRight: string[];

      if (editJoin) {
        initialRightModel = editJoin.right;
        initialJoinType = editJoin.how;
        initialKeyPairs = editJoin.on;
        initialSuffixes = editJoin.suffixes || ['_x', '_y'];
        rightCols = initialRightModel ? getColumnsForTarget(initialRightModel) : [];
        initialSelectedRight = [...rightCols];
      } else if (editSemijoin) {
        initialRightModel = editSemijoin.right;
        initialJoinType = 'semi';
        initialKeyPairs = editSemijoin.on;
        initialSuffixes = ['_x', '_y'];
        rightCols = initialRightModel ? getColumnsForTarget(initialRightModel) : [];
        initialSelectedRight = [...rightCols];
      } else if (editAntijoin) {
        initialRightModel = editAntijoin.right;
        initialJoinType = 'anti';
        initialKeyPairs = editAntijoin.on;
        initialSuffixes = ['_x', '_y'];
        rightCols = initialRightModel ? getColumnsForTarget(initialRightModel) : [];
        initialSelectedRight = [...rightCols];
      } else if (editLookup) {
        initialRightModel = editLookup.right;
        initialJoinType = 'lookup';
        initialKeyPairs = editLookup.on;
        initialSuffixes = ['_x', '_y'];
        rightCols = initialRightModel ? getColumnsForTarget(initialRightModel) : [];
        initialSelectedRight = editLookup.values || [...rightCols];
      } else {
        // New open
        initialRightModel = availableTargets[0]?.id || null;
        initialJoinType = 'left';
        initialSuffixes = ['_x', '_y'];
        rightCols = initialRightModel ? getColumnsForTarget(initialRightModel) : [];
        initialSelectedRight = [...rightCols];
        // Auto-match columns by identical names
        const autoMatched = findMatchingColumns(leftCols, rightCols);
        initialKeyPairs = autoMatched.length > 0 ? autoMatched : [[null, null]];
      }

      return {
        leftModel: signal<string | null>(leftModelId),
        rightModel: signal<string | null>(initialRightModel),
        joinType: signal<JoinType>(initialJoinType),
        keyPairs: signal<(string | null)[][]>(initialKeyPairs),
        suffixes: signal<string[]>(initialSuffixes),
        targets: signal<JoinTarget[]>(availableTargets),
        leftColumns: signal<string[]>(leftCols),
        rightColumns: signal<string[]>(rightCols),
        selectedLeftColumns: signal<string[]>([...leftCols]),
        selectedRightColumns: signal<string[]>(initialSelectedRight),
        saveAsNewModel: signal(false),
        previewData: signal<any | null>(null),
        previewError: signal<string | null>(null),
        isPreviewing: signal(false),
        keyPairAnalysis: signal<KeyPairAnalysis[]>([]),
        previewTableId: signal<string | null>(null),
        previewMismatchValues: signal<MismatchPreview | null>(null),
      };
    },
    {
      hasError: (s) => {
        const hasRight = !!s.rightModel.value;
        const hasKeys = s.joinType.value === 'cross' || s.keyPairs.value.some((p) => p[0] && p[1]);
        const hasLookupValues =
          s.joinType.value !== 'lookup' || s.selectedRightColumns.value.length > 0;
        return !hasRight || !hasKeys || !hasLookupValues;
      },
      getState: (s) => ({
        leftModel: s.leftModel.value,
        rightModel: s.rightModel.value,
        joinType: s.joinType.value,
        keyPairs: s.keyPairs.value,
        suffixes: s.suffixes.value,
        selectedLeftColumns: s.selectedLeftColumns.value,
        selectedRightColumns: s.selectedRightColumns.value,
        saveAsNewModel: s.saveAsNewModel.value,
      }),
    }
  );

  const {
    leftModel,
    rightModel,
    joinType,
    keyPairs,
    suffixes,
    leftColumns,
    rightColumns,
    selectedLeftColumns,
    selectedRightColumns,
    saveAsNewModel,
    previewError,
    isPreviewing,
    keyPairAnalysis,
    previewTableId,
    previewMismatchValues,
  } = state;

  // Trigger async key analysis on mount
  useEffect(() => {
    analyzeJoinKeys(state);
  }, []);

  const activeModel = AppStore.activeModel.value;
  const activeSource = AppStore.activeSource.value;

  const handleLeftModelChange = (id: string) => {
    leftModel.value = id;
    const cols = getColumnsForTarget(id);
    leftColumns.value = cols;
    selectedLeftColumns.value = [...cols];
    previewData.value = null;
    previewError.value = null;
    keyPairAnalysis.value = [];
    keyPairs.value = reconcileKeyPairs(keyPairs.value, cols, rightColumns.value, 'left');
    analyzeJoinKeys(state);
  };

  const handleRightModelChange = (id: string) => {
    rightModel.value = id;
    const cols = getColumnsForTarget(id);
    rightColumns.value = cols;
    selectedRightColumns.value = [...cols];
    previewData.value = null;
    previewError.value = null;
    keyPairAnalysis.value = [];
    keyPairs.value = reconcileKeyPairs(keyPairs.value, leftColumns.value, cols, 'right');
    analyzeJoinKeys(state);
  };

  const handleJoinTypeChange = (type: JoinType) => {
    joinType.value = type;
    // Re-analyze if not cross join
    if (type !== 'cross') {
      analyzeJoinKeys(state);
    }
  };

  const handleSuffixChange = (index: number, value: string) => {
    const newSuffixes = [...suffixes.value];
    newSuffixes[index] = value;
    suffixes.value = newSuffixes;
  };

  const updateKeyPair = (index: number, position: 0 | 1, value: string | null) => {
    const newPairs = keyPairs.value.map((pair, i) => {
      if (i === index) {
        const newPair = [...pair];
        newPair[position] = value;
        return newPair;
      }
      return pair;
    });
    keyPairs.value = newPairs;
    analyzeJoinKeys(state);
  };

  const handlePreview = () => {
    previewJoin(state);
  };

  // Destructure previewData separately to avoid JSX reference issues
  const { previewData } = state;

  // Column selection handlers
  const toggleLeftColumn = (col: string) => {
    const selected = selectedLeftColumns.value;
    selectedLeftColumns.value = selected.includes(col)
      ? selected.filter((c) => c !== col)
      : [...selected, col];
  };

  const toggleRightColumn = (col: string) => {
    const selected = selectedRightColumns.value;
    selectedRightColumns.value = selected.includes(col)
      ? selected.filter((c) => c !== col)
      : [...selected, col];
  };

  const selectAllLeftColumns = () => {
    selectedLeftColumns.value = [...leftColumns.value];
  };

  const selectNoneLeftColumns = () => {
    selectedLeftColumns.value = [];
  };

  const selectAllRightColumns = () => {
    selectedRightColumns.value = [...rightColumns.value];
  };

  const selectNoneRightColumns = () => {
    selectedRightColumns.value = [];
  };

  // Get current left model ID (from active model or source)
  const currentLeftId = activeModel?.id || activeSource?.id || null;
  const effectiveLeftId = leftModel.value || currentLeftId;

  return (
    <div class={joinStyles.joinDialog}>
      {/* Two-column layout for source/model selection */}
      <div class={joinStyles.sourceSelectorGrid}>
        {/* Left Side */}
        <div class={joinStyles.sourceSelector}>
          <label class={styles.label}>{t('join.leftTable')}</label>
          <div class={joinStyles.currentSelection}>
            {effectiveLeftId && (
              <div class={joinStyles.currentSelectionItem}>
                {activeModel && effectiveLeftId === activeModel.id ? (
                  <>
                    <span class={joinStyles.icon}>📊</span>
                    <span class={joinStyles.name}>{activeModel.name}</span>
                  </>
                ) : activeSource && effectiveLeftId === activeSource.id ? (
                  <>
                    <span class={joinStyles.icon}>📄</span>
                    <span class={joinStyles.name}>{activeSource.name}</span>
                  </>
                ) : (
                  <span class={joinStyles.name}>Selected</span>
                )}
              </div>
            )}
          </div>
          <JoinTreeSelector
            selectedId={effectiveLeftId}
            onSelect={handleLeftModelChange}
            excludeId={rightModel.value}
            onPreview={(id) => {
              previewTableId.value = id;
            }}
          />
        </div>

        {/* Right Side */}
        <div class={joinStyles.sourceSelector}>
          <label class={styles.label}>{t('join.rightTable')}</label>
          <JoinTreeSelector
            selectedId={rightModel.value}
            onSelect={handleRightModelChange}
            excludeId={effectiveLeftId}
            onPreview={(id) => {
              previewTableId.value = id;
            }}
          />
        </div>
      </div>

      {/* Join Type */}
      <JoinTypeSelector joinType={joinType} onChange={handleJoinTypeChange} />

      {/* Join Keys */}
      {joinType.value !== 'cross' && (
        <JoinKeysEditor
          keyPairs={keyPairs.value as [string | null, string | null][]}
          leftColumns={leftColumns.value}
          rightColumns={rightColumns.value}
          keyPairAnalysis={keyPairAnalysis.value}
          onUpdate={updateKeyPair}
          onAdd={() => {
            keyPairs.value = [...keyPairs.value, [null, null]];
            analyzeJoinKeys(state);
          }}
          onRemove={(index) => {
            if (keyPairs.value.length > 1) {
              keyPairs.value = keyPairs.value.filter((_, i) => i !== index);
              analyzeJoinKeys(state);
            }
          }}
          onShowMismatch={(values, column, side) => {
            if (values.length > 0) {
              previewMismatchValues.value = { values, column, side };
            }
          }}
        />
      )}

      {/* Column Selection */}
      <div class={joinStyles.columnSelectionGrid}>
        {/* Left Columns */}
        <JoinColumnSelector
          label={t('join.leftColumnsLabel')}
          columns={leftColumns.value}
          selectedColumns={selectedLeftColumns.value}
          onToggle={toggleLeftColumn}
          onSelectAll={selectAllLeftColumns}
          onSelectNone={selectNoneLeftColumns}
        />

        {/* Right Columns */}
        {joinType.value !== 'semi' && joinType.value !== 'anti' && (
          <JoinColumnSelector
            label={t('join.rightColumnsLabel')}
            columns={rightColumns.value}
            selectedColumns={selectedRightColumns.value}
            onToggle={toggleRightColumn}
            onSelectAll={selectAllRightColumns}
            onSelectNone={selectNoneRightColumns}
          />
        )}
      </div>

      {/* Column Suffixes */}
      <div class={styles.group}>
        <label class={styles.label}>{t('join.suffixesLabel')}</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            class={styles.input}
            style={{ flex: 1 }}
            value={suffixes.value[0]}
            onInput={(e) => handleSuffixChange(0, e.currentTarget.value)}
            placeholder={t('join.suffixPlaceholder.left')}
          />
          <span>/</span>
          <input
            type="text"
            class={styles.input}
            style={{ flex: 1 }}
            value={suffixes.value[1]}
            onInput={(e) => handleSuffixChange(1, e.currentTarget.value)}
            placeholder={t('join.suffixPlaceholder.right')}
          />
        </div>
        <div class={styles.helpText}>{t('join.suffixesHelp')}</div>
      </div>

      {/* Save as New Model */}
      <div class={styles.group}>
        <label class={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={saveAsNewModel.value}
            onChange={(e) => {
              saveAsNewModel.value = e.currentTarget.checked;
            }}
          />
          <span>{t('join.saveAsNew')}</span>
        </label>
      </div>

      {/* Inline Help */}
      <div class={styles.expressionHelp}>
        <div class={styles.expressionHelpTitle}>
          <span>{t('join.types.title')}</span>
        </div>
        <div class={styles.exampleGrid} style={{ fontFamily: 'var(--font-family)' }}>
          <div>
            <strong>{t('join.types.left')}</strong>
          </div>
          <div class={styles.exampleDescription}>{t('join.types.leftDesc')}</div>
          <div>
            <strong>{t('join.types.right')}</strong>
          </div>
          <div class={styles.exampleDescription}>{t('join.types.rightDesc')}</div>
          <div>
            <strong>{t('join.types.inner')}</strong>
          </div>
          <div class={styles.exampleDescription}>{t('join.types.innerDesc')}</div>
          <div>
            <strong>{t('join.types.full')}</strong>
          </div>
          <div class={styles.exampleDescription}>{t('join.types.fullDesc')}</div>
          <div>
            <strong>{t('join.types.semi')}</strong>
          </div>
          <div class={styles.exampleDescription}>{t('join.types.semiDesc')}</div>
          <div>
            <strong>{t('join.types.anti')}</strong>
          </div>
          <div class={styles.exampleDescription}>{t('join.types.antiDesc')}</div>
        </div>
      </div>

      {/* Preview Button */}
      <div class={styles.group}>
        <button
          class="button button--secondary"
          onClick={handlePreview}
          disabled={isPreviewing.value || !rightModel.value}
        >
          {isPreviewing.value ? t('join.previewing') : t('join.preview')}
        </button>
      </div>

      {/* Preview Error */}
      {previewError.value && <div class={styles.error}>{previewError.value}</div>}

      {/* Table Preview Modal */}
      <TablePreviewModal
        previewTableId={previewTableId}
        previewMismatchValues={previewMismatchValues}
      />
    </div>
  );
}
